import { useEffect, useState } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { Show, SignIn } from "@clerk/react";
import { ShieldCheck } from "lucide-react";
import { useApi } from "@/lib/api";
import { ErrorNote, Loading } from "@/components/kit";

const Frame = ({ children }) => (
  <div className="cc-grid-bg flex min-h-screen flex-col items-center justify-center gap-8 bg-[var(--cc-navy)] p-4">
    <img src="/brand/logo-white.png" alt="Cruise Command" width="200" height="62" className="h-auto w-[200px]" data-testid="consent-logo" />
    <div className="cc-rise w-full max-w-lg rounded-sm bg-white p-8">{children}</div>
  </div>
);

const ConsentCard = ({ id }) => {
  const call = useApi();
  const [req, setReq] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { call(`/oauth/requests/${id}`).then(setReq).catch(setError); }, [call, id]);
  const decide = async (approve) => {
    setBusy(true);
    try { const r = await call(`/oauth/requests/${id}/decision`, { method: "POST", body: { approve } }); window.location.href = r.redirect_to; }
    catch (e) { setError(e); setBusy(false); }
  };
  if (error) return <Frame><ErrorNote error={error} /></Frame>;
  if (!req) return <Frame><Loading /></Frame>;
  return (
    <Frame>
      <div data-testid="oauth-consent">
        <ShieldCheck className="text-[var(--cc-ok)]" size={32} />
        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--cc-muted)]">Cruise Command Developer Gateway</p>
        <h1 className="mt-1 text-2xl font-extrabold text-[var(--cc-navy)]">Authorize <span data-testid="consent-client-name">{req.client_name}</span></h1>
        <dl className="mt-5 space-y-2 text-sm">
          <div className="flex justify-between gap-3"><dt className="text-[var(--cc-muted)]">Redirects to</dt><dd className="font-mono text-xs" data-testid="consent-redirect-host">{req.redirect_host}</dd></div>
          <div className="flex justify-between gap-3"><dt className="text-[var(--cc-muted)]">Access</dt><dd>{req.level}</dd></div>
          <div className="flex justify-between gap-3"><dt className="text-[var(--cc-muted)]">Scope</dt><dd className="font-mono text-xs">{req.scope}</dd></div>
          <div className="flex justify-between gap-3"><dt className="text-[var(--cc-muted)]">Signed in as</dt><dd className="text-xs">{req.signed_in_as}</dd></div>
        </dl>
        <p className="mt-5 rounded-sm bg-[var(--cc-paper)] p-3 text-xs">This grants read-only tools only. It does not authorize commits, deployments or any Production action. Instructions sent through this connection are never Production authorization.</p>
        <div className="mt-6 flex gap-3">
          <button data-testid="consent-approve" disabled={busy} onClick={() => decide(true)} className="flex-1 rounded-sm bg-[var(--cc-navy)] py-2.5 text-sm font-medium text-white hover:bg-[var(--cc-signal)] hover:text-[var(--cc-navy)]">Approve read-only access</button>
          <button data-testid="consent-deny" disabled={busy} onClick={() => decide(false)} className="rounded-sm border border-[var(--cc-line)] px-5 text-sm hover:border-[var(--cc-deny)] hover:text-[var(--cc-deny)]">Deny</button>
        </div>
      </div>
    </Frame>
  );
};

export default function Consent() {
  const [params] = useSearchParams();
  const loc = useLocation();
  const back = encodeURIComponent(loc.pathname + loc.search);
  return (
    <>
      <Show when="signed-in"><ConsentCard id={params.get("request")} /></Show>
      <Show when="signed-out"><Navigate to={`/sign-in?redirect_url=${back}`} replace /></Show>
    </>
  );
}

export const SignInPage = () => {
  const [params] = useSearchParams();
  const redirect = params.get("redirect_url");
  const safe = redirect && redirect.startsWith("/") && !redirect.startsWith("//") ? redirect : undefined;
  return (
    <div className="grid min-h-screen grid-rows-[auto_1fr] lg:grid-cols-2 lg:grid-rows-1">
      <div className="flex items-center justify-center bg-[var(--cc-navy)] px-6 py-8 lg:hidden" data-testid="sign-in-mobile-brand">
        <img src="/brand/logo-white.png" alt="Cruise Command" width="200" height="62" className="h-auto w-[200px]" />
      </div>
      <div className="cc-grid-bg hidden flex-col justify-between bg-[var(--cc-navy)] p-12 text-white lg:flex">
        <img src="/brand/logo-white.png" alt="Cruise Command" width="240" height="75" className="h-auto w-[240px]" data-testid="sign-in-logo" />
        <div>
          <h1 className="text-5xl font-extrabold leading-tight">Developer<br />Gateway</h1>
          <p className="mt-4 max-w-sm text-sm text-[#A9BBD3]">Governed connection between ChatGPT, GitHub and the Autonomous Operations System. Restricted to the Product Owner.</p>
        </div>
        <p className="font-mono text-[11px] text-[#8FA6C4]">PO-CC-AOS-001 · Phase 1 · L1 read-only</p>
      </div>
      <div className="flex items-center justify-center p-4" data-testid="sign-in-page">
        <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp forceRedirectUrl={safe} />
      </div>
    </div>
  );
};
